import Styles from '../styles.module.css';
import React, { useEffect, useState } from 'react';
import {
  Timestamp, arrayRemove, doc, getDoc, getFirestore, updateDoc,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { IoCalendarOutline, IoChatbubbleEllipsesOutline, IoChevronDown, IoChevronUp, IoListOutline, IoPersonAddOutline, IoPersonOutline, IoTrashOutline } from 'react-icons/io5';
import { useAppSelector } from 'src/salon/app/hook';
import { unsavedGuard } from '../../unsavedGuard';
import VisitSettings from './VisitSettings';
import DeadlinePicker, { formatDeadline } from './DeadlinePicker';
import {
  APPLICATION_MESSAGE_TEMPLATES, COUPON_MESSAGE_TEMPLATES, PERIOD_STATE_LABELS, SESSION_MESSAGE_TEMPLATES, RecruitPeriod, StatusMessageKey, StatusMessageTemplate, StatusMessages,
  VISIT_MESSAGE_TEMPLATES, deadlineEnd, examinationLabel, parseRecruitPeriods, resolvePeriods, resolveStatusMessages,
} from '../types';

// 求人ごとの選考段階(1〜5次)・審査項目の設定 + 審査員管理画面。
// 選考段階設定は recruits/{companyId}_2027 に exam_selection{n}Count / exam_selection{n}Text{1-8}
// を追記する（既存の求人編集画面 src/salon/desktop/3.recruit/ とは別の書き込み経路。
// 既存の求人編集はupdateDocで自分のトップレベルキーのみ更新するため衝突しない）。
//
// ステータス変更時のメッセージ(テンプレート)も同じ recruits/{companyId}_2027 の statusMessages に保存する。
// 募集回(締切日)も同じドキュメントの recruitPeriods に保存する(仕組みは types.ts の「募集回」)。
//
// 並び順は「募集回 → 選考段階の審査項目 → サロン見学(フォーム・審査項目) → ステータス変更時のメッセージ → 審査員管理」。
// サロン見学フォーム(VisitSettings)と審査員管理は変更した時点で保存し、それ以外は「設定を保存」で保存する。
//
// 審査員管理は companies/{companyId} の judgeInviteEmails / judgeInvites / judgeUids にまとめる。
// 追加時はCloud Functions(createJudgeAccount)がFirebase Authアカウントを作成し、
// 英数字8桁の初期パスワードをメールで送る(審査員自身の/company/judge-signinでの
// 自己登録は行わない)。登録後は judgeUids の uid 検索で審査員セッションの会社を解決する。

type StageConfig = { count: number; texts: string[] };
type JudgeEntry = {
  email: string;
  name: string;
  uid: string;
  status: 'invited' | 'claimed';
  createdAt: any;
  addedAt?: string;
};

const STAGES = [1, 2, 3, 4, 5];

// 'YYYY-MM-DD' に日数を足す(締切日の入力欄の min/max 用)
function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// 次の回の締切日として選べる一番早い日('YYYY-MM-DD')。前の回の終わり(締切日の終わり・締めた日時の早いほう)より後。
// 締切日より前に締めた回の次は、締めた日から選べる(例: 10/15締切の回を10/6に締めたら、次の回は10/6から)。
function firstDeadlineAfter(prev: RecruitPeriod | undefined): string | undefined {
  if (!prev) return undefined;
  const ends = [deadlineEnd(prev.deadline), prev.closedAt].filter((d): d is Date => d !== null);
  if (ends.length === 0) return undefined;
  const end = new Date(Math.min(...ends.map((d) => d.getTime())));
  // 締切日で終わる回は翌日から、締めて終わる回は締めた日(日本時間)から
  if (prev.closedAt && end.getTime() === prev.closedAt.getTime()) {
    return new Date(end.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
  }
  return addDays(prev.deadline, 1);
}

// 締切日は前の回より後・次の回より前でないといけない。問題があればその内容を返す。
function periodsError(periods: RecruitPeriod[]): string {
  for (let i = 1; i < periods.length; i++) {
    const prev = periods[i - 1];
    const cur = periods[i].deadline;
    if (!prev.deadline && !prev.closedAt) return `第${i}回募集の締切日がないため、第${i + 1}回募集を作れません。`;
    const min = firstDeadlineAfter(prev);
    if (min && cur && cur < min) return `第${i + 1}回募集の締切日は、${min.replace(/-/g, '/')}以降にしてください。`;
  }
  return '';
}

// 保存用。最後に何も設定していない回が続く場合は落とす(保存しなくても「次の回」として自動で出るため)。
function periodsForSave(periods: RecruitPeriod[]) {
  const list = [...periods];
  while (list.length > 1 && !list[list.length - 1].deadline && !list[list.length - 1].closedAt) list.pop();
  return list.map((p) => ({ deadline: p.deadline, closedAt: p.closedAt ? Timestamp.fromDate(p.closedAt) : null }));
}

function emptyStage(): StageConfig {
  return { count: 0, texts: new Array(8).fill('') };
}

function emptyStages(): Record<number, StageConfig> {
  const initial: Record<number, StageConfig> = {};
  STAGES.forEach((s) => { initial[s] = emptyStage(); });
  return initial;
}

const EvaluationSettings: React.FC = () => {

  const company = useAppSelector((state) => state.company.company);
  const recruitId = company?.id ? `${company.id}_2027` : '';

  const [ stages, setStages ] = useState<Record<number, StageConfig>>(emptyStages);
  // サロン見学時の審査項目。応募の選考段階(1〜5次)とは異なり複数ラウンドは用意せず、
  // recruits/{recruitId}.visitExamCount / visitExamText{1-8} の1セットだけを持つ。
  const [ visitStage, setVisitStage ] = useState<StageConfig>(emptyStage());
  const [ statusMessages, setStatusMessages ] = useState<StatusMessages>(() => resolveStatusMessages(undefined));
  const [ periods, setPeriods ] = useState<RecruitPeriod[]>(() => parseRecruitPeriods(undefined));
  const [ openMessageKey, setOpenMessageKey ] = useState<StatusMessageKey | null>(null);
  const [ loading, setLoading ] = useState(true);
  const [ saving, setSaving ] = useState(false);
  const [ saveError, setSaveError ] = useState('');
  // 「設定を保存」で保存する内容(審査項目・ステータス変更時のメッセージ)の、最後に読み込んだ・保存した時点の値。
  // 今の値と違えば未保存。応募設定を閉じる・サイドバーで別のページへ移るときに確認を出す。
  const [ savedSnapshot, setSavedSnapshot ] = useState('');
  const [ saved, setSaved ] = useState(false);

  const [ judges, setJudges ] = useState<JudgeEntry[]>([]);
  const [ judgeClaimed, setJudgeClaimed ] = useState<Record<string, boolean>>({});
  const [ newJudgeEmail, setNewJudgeEmail ] = useState('');
  const [ newJudgeName, setNewJudgeName ] = useState('');
  const [ judgeError, setJudgeError ] = useState('');
  const [ judgeSaving, setJudgeSaving ] = useState(false);

  useEffect(() => {
    if (!recruitId) return;
    (async () => {
      setLoading(true);
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'recruits', recruitId));
      const data = snap.data();

      const next = emptyStages();
      STAGES.forEach((s) => {
        next[s] = {
          count: data?.[`exam_selection${s}Count`] ?? 0,
          texts: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => data?.[`exam_selection${s}Text${i}`] ?? ''),
        };
      });
      setStages(next);

      setVisitStage({
        count: data?.visitExamCount ?? 0,
        texts: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => data?.[`visitExamText${i}`] ?? ''),
      });

      const loadedMessages = resolveStatusMessages(data?.statusMessages);
      setStatusMessages(loadedMessages);
      const loadedPeriods = parseRecruitPeriods(data?.recruitPeriods);
      setPeriods(loadedPeriods);
      setSavedSnapshot(JSON.stringify({
        periods: loadedPeriods,
        stages: next,
        visitStage: { count: data?.visitExamCount ?? 0, texts: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => data?.[`visitExamText${i}`] ?? '') },
        statusMessages: loadedMessages,
      }));

      const companySnap = await getDoc(doc(db, 'companies', company.id));
      const companyData = companySnap.data();
      const companyJudges: JudgeEntry[] = Array.isArray(companyData?.judgeInvites) ? companyData!.judgeInvites : [];
      const legacyJudges: JudgeEntry[] = Array.isArray(data?.judges)
        ? data!.judges.map((j: any) => ({
          email: j.email,
          name: j.name,
          uid: '',
          status: 'invited',
          createdAt: j.addedAt ?? Timestamp.now(),
          addedAt: j.addedAt,
        }))
        : [];
      const judgeList = companyJudges.length > 0 ? companyJudges : legacyJudges;
      setJudges(judgeList);
      setJudgeClaimed(Object.fromEntries(judgeList.map((j) => [j.email, !!j.uid || j.status === 'claimed'])));
      setLoading(false);
    })();
  }, [recruitId, company.id]);

  const currentSnapshot = JSON.stringify({ periods, stages, visitStage, statusMessages });
  const dirty = !loading && savedSnapshot !== '' && currentSnapshot !== savedSnapshot;

  // サイドバーでの移動・応募設定を閉じるときの確認(unsavedGuard)と、タブを閉じる・再読み込みするときの確認
  useEffect(() => {
    unsavedGuard.setDirty(dirty);
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
  useEffect(() => () => unsavedGuard.setDirty(false), []);

  function setCount(stage: number, count: number) {
    setStages((prev) => ({ ...prev, [stage]: { ...prev[stage], count } }));
  }

  function setText(stage: number, index: number, value: string) {
    setStages((prev) => ({
      ...prev,
      [stage]: { ...prev[stage], texts: prev[stage].texts.map((t, i) => i === index ? value : t) },
    }));
  }

  function setVisitCount(count: number) {
    setVisitStage((prev) => ({ ...prev, count }));
  }

  function setVisitText(index: number, value: string) {
    setVisitStage((prev) => ({ ...prev, texts: prev.texts.map((t, i) => i === index ? value : t) }));
  }

  async function save() {
    if (!recruitId) return;
    setSaving(true);
    const db = getFirestore();
    const periodError = periodsError(periods);
    if (periodError) {
      setSaveError(periodError);
      setSaving(false);
      return;
    }
    const payload: Record<string, any> = {};
    STAGES.forEach((s) => {
      payload[`exam_selection${s}Count`] = stages[s].count;
      for (let i = 0; i < 8; i++) {
        payload[`exam_selection${s}Text${i + 1}`] = stages[s].texts[i];
      }
    });
    payload.visitExamCount = visitStage.count;
    for (let i = 0; i < 8; i++) {
      payload[`visitExamText${i + 1}`] = visitStage.texts[i];
    }
    payload.statusMessages = statusMessages;
    payload.recruitPeriods = periodsForSave(periods);
    setSaveError('');
    try {
      await updateDoc(doc(db, 'recruits', recruitId), payload);
    } catch (e) {
      console.error('応募設定の保存に失敗しました', e);
      setSaving(false);
      setSaveError('保存できませんでした。通信状況を確認して、もう一度お試しください。');
      return;
    }
    setSavedSnapshot(currentSnapshot);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  async function addJudge() {
    setJudgeError('');
    // 日本語IMEで全角の英数字・「＠」・「．」や途中の空白、コピペで紛れ込む
    // ゼロ幅スペース/BOM/制御文字が混ざったまま送ると、Firebase Auth に
    // auth/invalid-email で弾かれてしまう。半角化(NFKC)＋不可視文字/空白の
    // 除去をしてから扱う(サーバー側 functions/judgeInvite.js と同じ正規化)。
    const email = newJudgeEmail
      .normalize('NFKC')
      .replace(/[\s\u0000-\u001F\u007F\u00AD\u200B-\u200F\u202A-\u202E\u2060\uFEFF]/g, '')
      // NFKCで「@」に寄らない類似記号(小記号@ U+FE6B 等)を @ に、
      // 日本語IMEで「.」の代わりに入りやすい句点・中黒類を . に寄せる
      .replace(/[\uFE6B\uFF20]/g, '@')
      .replace(/[\u3002\uFF61\u30FB\uFF65\u2027\u00B7\u2022]/g, '.')
      .toLowerCase();
    const name = newJudgeName.trim();

    if (!email || !name) {
      setJudgeError('メールアドレスと名前の両方を入力してください。');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      // どの文字が原因か特定できるよう、メールアドレスとして普通に使う文字以外の
      // コードポイントだけを開発者コンソールに出す(メールアドレス自体は出さない)
      // eslint-disable-next-line no-console
      console.warn('[addJudge] invalid email input', JSON.stringify({
        unusualCodePoints: Array.from(newJudgeEmail)
          .filter((c) => !/[A-Za-z0-9@._+-]/.test(c))
          .map((c) => c.codePointAt(0)?.toString(16)),
      }));
      setJudgeError('メールアドレスの形式が正しくありません。半角の「@」と「.」で入力し直してください。');
      return;
    }

    setJudgeSaving(true);
    try {
      // Firebase Authアカウントの作成・パスワード発行・招待メール送信・
      // companies.judgeInvites等への保存まで、Cloud Functions側で行う。
      const functions = getFunctions(getApp(), 'asia-northeast1');
      const createJudgeAccount = httpsCallable(functions, 'createJudgeAccount');
      const result = await createJudgeAccount({ companyId: company.id, email, name });
      const uid = (result.data as { uid?: string })?.uid ?? '';

      const nextJudge: JudgeEntry = {
        email,
        name,
        uid,
        status: 'claimed',
        createdAt: Timestamp.now(),
      };
      const nextJudges = [...judges.filter((j) => j.email !== email), nextJudge];

      setJudges(nextJudges);
      setJudgeClaimed((prev) => ({ ...prev, [email]: true }));
      setNewJudgeEmail('');
      setNewJudgeName('');
    } catch (error: any) {
      const code = error?.code ?? '';
      setJudgeError(
        // already-exists は「他社の審査員として登録済み」と「memorii の別のアカウント
        // (会社オーナー・学生・学校)で使われている」の2通りあるため、関数側の文言を出す。
        code === 'functions/already-exists'
          ? (error?.message || 'このメールアドレスは既に他社の審査員として登録されています。')
          : code === 'functions/invalid-argument'
            ? (error?.message || 'メールアドレスの形式が正しくありません。')
            : '審査員の登録に失敗しました。時間をおいて再度お試しください。'
      );
    } finally {
      setJudgeSaving(false);
    }
  }

  async function removeJudge(email: string) {
    const db = getFirestore();
    const target = judges.find((j) => j.email === email);
    const nextJudges = judges.filter((j) => j.email !== email);
    const payload: Record<string, any> = {
      judgeInviteEmails: arrayRemove(email),
      judgeInvites: nextJudges,
    };
    if (target?.uid) {
      payload.judgeUids = arrayRemove(target.uid);
    }
    await updateDoc(doc(db, 'companies', company.id), payload);
    setJudges(nextJudges);
    setJudgeClaimed((prev) => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
  }

  // 締切日を変える。index が保存済みの回の数と同じなら、まだ保存していない次の回に締切日を入れる(=その回を作る)。
  function setPeriodDeadline(index: number, deadline: string) {
    setSaveError('');
    setPeriods((prev) => {
      if (index >= prev.length) return [...prev, { deadline, closedAt: null }];
      return prev.map((p, i) => (i === index ? { ...p, deadline } : p));
    });
  }

  // 締切日を未設定に戻す。途中の回の締切日を消すと後ろの回に応募が入らなくなるため、後ろの回もまとめて消す
  // (後ろの回を締めている場合は消せない。canClearPeriod で判定)。
  function clearPeriodDeadline(index: number) {
    const later = periods.slice(index + 1).filter((p) => p.deadline || p.closedAt);
    if (later.length > 0) {
      const names = periods.slice(index + 1).map((_, k) => `第${index + k + 2}回募集`).join('・');
      if (!window.confirm(`締切日を未設定にすると、${names}の締切日も消え、応募はすべて第${index + 1}回募集に入ります。よろしいですか？`)) return;
    }
    setSaveError('');
    setPeriods((prev) => prev.slice(0, index + 1).map((p, i) => (i === index ? { ...p, deadline: '' } : p)));
  }

  const resolvedPeriods = resolvePeriods(periods);

  const renderMessageTemplate = (t: StatusMessageTemplate) => {
    const open = openMessageKey === t.key;
    const text = statusMessages[t.key];
    return (
      <div className={Styles.visitCard} key={t.key}>
        <button
          type="button"
          className={`${Styles.visitRow} ${Styles.visitRowMain} ${Styles.messageTemplateHead}`}
          onClick={() => setOpenMessageKey(open ? null : t.key)}
        >
          <IoChatbubbleEllipsesOutline className={Styles.visitIcon} />
          <div className={Styles.visitRowText}>
            <span className={`${Styles.visitRowTitle} ${Styles.visitRowTitleMain}`}>{t.label}</span>
            <p className={Styles.visitRowDesc}>{t.description}</p>
          </div>
          <span className={text.trim() ? Styles.messageTemplateOn : Styles.messageTemplateOff}>
            {text.trim() ? '設定あり' : '使わない'}
          </span>
          {open ? <IoChevronUp className={Styles.messageTemplateChevron} /> : <IoChevronDown className={Styles.messageTemplateChevron} />}
        </button>

        {open && (
          <div className={Styles.messageTemplateBody}>
            <textarea
              className={Styles.messageTemplateTextarea}
              value={text}
              rows={9}
              placeholder="空にすると、このステータスに変えてもメッセージは入りません"
              onChange={(e) => setStatusMessages((prev) => ({ ...prev, [t.key]: e.target.value }))}
            />
            <div className={Styles.messageTemplateActions}>
              <button type="button" onClick={() => setStatusMessages((prev) => ({ ...prev, [t.key]: t.defaultText }))}>例文に戻す</button>
              <button type="button" onClick={() => setStatusMessages((prev) => ({ ...prev, [t.key]: '' }))}>使わない（空にする）</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!recruitId) {
    return <p className={Styles.empty}>求人情報が見つかりません。</p>;
  }

  if (loading) {
    return <p className={Styles.empty}>読み込み中...</p>;
  }

  return (
    <div className={Styles.settings}>
      <p className={Styles.settingsSaveGuide}>
        募集回・審査項目・ステータス変更時のメッセージは、変更したら下の「設定を保存」で保存します。
        サロン見学フォームと審査員管理は、変更した時点で保存されます。
      </p>

      <div>
        <h4 className={Styles.visitPageTitle}>募集回</h4>
        <p className={Styles.visitPageDesc}>
          締切日を入れると、締切日の翌日以降に届いた応募は自動で次の募集回（第2回募集など）に入ります。
          締切日を入れなければ、同じ募集回のまま受け付け続けます。締切日を過ぎても、その募集回の選考はそのまま続けられます。
        </p>
      </div>

      <div className={Styles.visitCard}>
        {resolvedPeriods.map((p, i) => {
          const prev = resolvedPeriods[i - 1];
          const next = resolvedPeriods[i + 1];
          // 締切日を未設定にできるのは、後ろの回をまだ締めていないとき(後ろの回の締切日も一緒に消す)
          const canClear = p.saved && !!p.deadline && !periods.slice(i + 1).some((q) => q.closedAt);
          return (
            <React.Fragment key={i}>
              {i > 0 && <div className={Styles.visitDivider} />}
              <div className={`${Styles.visitRow} ${Styles.periodRow}`}>
                <IoCalendarOutline className={Styles.visitIcon} />
                <div className={Styles.visitRowText}>
                  <span className={`${Styles.visitRowTitle} ${Styles.visitRowTitleMain}`}>
                    {p.name}
                    <span className={`${Styles.periodState} ${Styles[`periodState_${p.state}`]}`}>{PERIOD_STATE_LABELS[p.state]}</span>
                  </span>
                  <p className={Styles.visitRowDesc}>
                    {p.closedAt
                      ? `${p.closedAt.getFullYear()}/${p.closedAt.getMonth() + 1}/${p.closedAt.getDate()} に締めました`
                      : p.deadline
                        ? `${formatDeadline(p.deadline)}までの応募が入ります`
                        : '締切日なし（次の募集回を作るまで受け付けます）'}
                  </p>
                </div>
                {!p.closedAt && (
                  <DeadlinePicker
                    value={p.deadline}
                    min={firstDeadlineAfter(prev)}
                    max={next?.saved && next.deadline ? addDays(next.deadline, -1) : undefined}
                    canClear={canClear}
                    label={`${p.name}の締切日`}
                    onChange={(v) => (v ? setPeriodDeadline(i, v) : clearPeriodDeadline(i))}
                  />
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className={Styles.settingsGroupDivider} />

      <div>
        <h4 className={Styles.visitPageTitle}>選考段階の審査項目</h4>
        <p className={Styles.visitPageDesc}>
          選考段階ごとに審査項目数（0〜8）と各項目のタイトルを設定します。「評価入力」タブでは
          ここで設定した項目数分の◎○△×とコメントを審査員が入力します。
        </p>
      </div>

      {STAGES.map((stage) => (
        <div className={Styles.visitCard} key={stage}>
          <div className={`${Styles.visitRow} ${Styles.visitRowMain}`}>
            <IoListOutline className={Styles.visitIcon} />
            <div className={Styles.visitRowText}>
              <span className={`${Styles.visitRowTitle} ${Styles.visitRowTitleMain}`}>{examinationLabel(stage)}の審査項目</span>
              <p className={Styles.visitRowDesc}>この段階で評価する項目の数を設定します</p>
            </div>
            <select
              className={Styles.settingsCountSelect}
              value={stages[stage].count}
              onChange={(e) => setCount(stage, Number(e.target.value))}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>{n === 0 ? '評価基準を設定しない' : `評価基準：${n}つ`}</option>
              ))}
            </select>
          </div>

          <div className={`${Styles.visitDetails} ${stages[stage].count > 0 ? Styles.visitDetailsOpen : ''}`}>
            <div className={Styles.visitDivider} />
            {stages[stage].texts.slice(0, stages[stage].count).map((text, i) => (
              <div className={Styles.visitRow} key={i}>
                <span className={Styles.settingsCriteriaBadge}>{i + 1}</span>
                <div className={Styles.visitRowText}>
                  <input
                    className={Styles.settingsCriteriaInput}
                    value={text}
                    placeholder={`審査項目${i + 1}のタイトル`}
                    onChange={(e) => setText(stage, i, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className={Styles.settingsGroupDivider} />

      <VisitSettings />

      <div className={Styles.settingsGroupDivider} />

      <div>
        <h4 className={Styles.visitPageTitle}>サロン見学の審査項目</h4>
        <p className={Styles.visitPageDesc}>
          サロン見学時の評価に使う項目数（0〜8）と各項目のタイトルを設定します。選考段階と異なり、
          見学の審査は複数ラウンド用意せず1回分のみです。
        </p>
      </div>

      <div className={Styles.visitCard}>
        <div className={`${Styles.visitRow} ${Styles.visitRowMain}`}>
          <IoListOutline className={Styles.visitIcon} />
          <div className={Styles.visitRowText}>
            <span className={`${Styles.visitRowTitle} ${Styles.visitRowTitleMain}`}>サロン見学の審査項目</span>
            <p className={Styles.visitRowDesc}>見学時に評価する項目の数を設定します</p>
          </div>
          <select
            className={Styles.settingsCountSelect}
            value={visitStage.count}
            onChange={(e) => setVisitCount(Number(e.target.value))}
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>{n === 0 ? '評価基準を設定しない' : `評価基準：${n}つ`}</option>
            ))}
          </select>
        </div>

        <div className={`${Styles.visitDetails} ${visitStage.count > 0 ? Styles.visitDetailsOpen : ''}`}>
          <div className={Styles.visitDivider} />
          {visitStage.texts.slice(0, visitStage.count).map((text, i) => (
            <div className={Styles.visitRow} key={i}>
              <span className={Styles.settingsCriteriaBadge}>{i + 1}</span>
              <div className={Styles.visitRowText}>
                <input
                  className={Styles.settingsCriteriaInput}
                  value={text}
                  placeholder={`審査項目${i + 1}のタイトル`}
                  onChange={(e) => setVisitText(i, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={Styles.settingsGroupDivider} />

      <div>
        <h4 className={Styles.visitPageTitle}>ステータス変更時のメッセージ</h4>
        <p className={Styles.visitPageDesc}>
          学生の詳細画面でステータスを変えると、ここで設定した文章がメッセージの入力欄に入ります。
          内容を確認して「送信」を押すだけで連絡できます（自動では送られません）。本文を空にしたステータスでは何も入りません。
        </p>
      </div>

      <p className={Styles.messageTemplateGroup}>応募</p>
      {APPLICATION_MESSAGE_TEMPLATES.map(renderMessageTemplate)}
      <p className={Styles.messageTemplateGroup}>サロン見学</p>
      {VISIT_MESSAGE_TEMPLATES.map(renderMessageTemplate)}
      <p className={Styles.messageTemplateGroup}>会社説明会</p>
      {SESSION_MESSAGE_TEMPLATES.map(renderMessageTemplate)}
      <p className={Styles.messageTemplateGroup}>クーポン</p>
      {COUPON_MESSAGE_TEMPLATES.map(renderMessageTemplate)}

      {/* 未保存の間は画面の下に貼り付けて、保存し忘れに気づけるようにする */}
      <div className={dirty ? `${Styles.settingsSaveRow} ${Styles.settingsSaveRowDirty}` : Styles.settingsSaveRow}>
        {saveError
          ? <span className={Styles.settingsSaveError}>{saveError}</span>
          : dirty && <span className={Styles.settingsSaveNotice}>保存していない変更があります</span>}
        <button type="button" className={Styles.settingsSaveButton} onClick={save} disabled={saving}>
          {saving ? '保存中...' : '設定を保存'}
        </button>
      </div>

      <div className={Styles.settingsGroupDivider} />

      <div>
        <h4 className={Styles.visitPageTitle}>審査員管理</h4>
        <p className={Styles.visitPageDesc}>
          名前とメールアドレスを登録すると、ログイン情報を記載した案内メールが自動送信されます。
          審査員は届いたメールアドレスと初期パスワードでログインすると、審査だけが使える
          専用画面が表示されます。パスワードはログイン後に本人が変更できます。
        </p>
      </div>

      <div className={Styles.visitCard}>
        {judges.length === 0 ? (
          <p className={Styles.empty}>登録済みの審査員はいません。</p>
        ) : (
          judges.map((j, i) => (
            <React.Fragment key={j.email}>
              <div className={Styles.visitRow}>
                <IoPersonOutline className={Styles.visitIcon} />
                <div className={Styles.visitRowText}>
                  <span className={Styles.visitRowTitle}>{j.name}</span>
                  <p className={Styles.visitRowDesc}>{j.email}</p>
                </div>
                <span className={Styles.stageBadge}>{judgeClaimed[j.email] ? '登録済み' : '招待中'}</span>
                <button type="button" className={Styles.judgeDeleteBtn} onClick={() => removeJudge(j.email)}>
                  <IoTrashOutline />
                </button>
              </div>
              {i < judges.length - 1 && <div className={Styles.visitDivider} />}
            </React.Fragment>
          ))
        )}
      </div>

      {judgeError && <p className={Styles.formError}>{judgeError}</p>}

      <div className={Styles.visitCard}>
        <div className={`${Styles.visitRow} ${Styles.visitRowMain}`}>
          <IoPersonAddOutline className={Styles.visitIcon} />
          <div className={Styles.visitRowText}>
            <span className={`${Styles.visitRowTitle} ${Styles.visitRowTitleMain}`}>審査員を追加</span>
            <div className={Styles.judgeAddInputs}>
              <input
                className={Styles.settingsCriteriaInput}
                placeholder="審査員の名前"
                value={newJudgeName}
                onChange={(e) => setNewJudgeName(e.target.value)}
              />
              <input
                className={Styles.settingsCriteriaInput}
                placeholder="審査員のメールアドレス"
                value={newJudgeEmail}
                onChange={(e) => setNewJudgeEmail(e.target.value)}
              />
            </div>
          </div>
          <button type="button" className={Styles.settingsSaveButton} onClick={addJudge} disabled={judgeSaving}>
            {judgeSaving ? '追加中...' : '追加'}
          </button>
        </div>
      </div>

      {saved && <div className={Styles.savedBanner}>保存しました。</div>}
    </div>
  );
};

export default EvaluationSettings;
